import { LEGACY_HOSTS, SITE_HOST, SITE_ORIGIN } from '@/constants/seo';

// 크로스도메인 로그인 브릿지.
//
// 카카오 로그인 콜백(api-online.spartaclub.kr)이 세션 쿠키를 .spartaclub.kr 에 심고
// 돌아오는데, hanghae.co 는 등록 도메인(eTLD+1)이 달라 그 쿠키를 읽지 못한다.
// 콜백 서버가 hanghae.co 용 쿠키를 심는 것은 브라우저 규칙상 불가능하므로,
// 구 도메인에 브릿지 페이지를 두고 거기서 쿠키를 읽어 프래그먼트로 넘겨받는다.
//
// 프래그먼트(#)를 쓰는 이유는 서버로 전송되지 않아 Referer·액세스 로그에
// 토큰이 남지 않기 때문이다. 쿼리스트링을 쓰면 전부 기록된다.
//
// 한시적 코드다. 인증 인프라가 hanghae.co 를 직접 지원하게 되면
// (LoginProcess 의 redirect_uri 를 주입할 수 있게 되거나 토큰 핸드오프가 생기면)
// 이 모듈과 pages/auth/* 를 함께 제거한다.

// 로그인 쿠키가 심기는 도메인. 브릿지 페이지는 여기서 동작해야 쿠키를 읽을 수 있다.
export const LEGACY_AUTH_ORIGIN = `https://${LEGACY_HOSTS[0]}`;

export const BRIDGE_PATH = '/auth/bridge';
export const CALLBACK_PATH = '/auth/callback';

// 넘겨받을 쿠키. 로그인 직후 .spartaclub.kr 에 심기는 것들이다.
export const HANDOFF_COOKIE_NAMES = ['token', 'refresh', 'userinfo'] as const;

// 로그인을 구 도메인에서 해야 하는 페이지. 정본 도메인에서 열리면 구 도메인의 같은 경로로 보낸다.
export const LEGACY_AUTH_PAGE_PATHS = ['/login', '/signup_kko'];

// 브릿지가 토큰을 돌려보낼 수 있는 오리진. 여기 없는 곳으로는 보내지 않는다.
// 검증을 빼면 next 파라미터를 통한 오픈 리다이렉트로 토큰이 외부에 새어나간다.
const ALLOWED_RETURN_HOSTS = [SITE_HOST, `www.${SITE_HOST}`];

/**
 * 로그인 쿠키를 직접 받을 수 없어 브릿지를 거쳐야 하는 호스트인지.
 * 클라이언트(needsAuthBridge)와 서버(middleware, framer 프록시)가 같은 기준을 쓴다.
 */
export const isBridgedHost = (hostname: string) => {
  const host = hostname.split(':')[0].toLowerCase();
  if (host === 'spartaclub.kr' || host.endsWith('.spartaclub.kr')) {
    return false;
  }
  // 정본 도메인이 아직 교체되지 않은 배포에서는 브릿지를 태우지 않는다.
  // 토큰을 돌려받을 수 없는 주소로 사용자를 보내게 되기 때문이다.
  return ALLOWED_RETURN_HOSTS.includes(host);
};

/**
 * 현재 문서가 로그인 쿠키를 직접 받을 수 없는 도메인에 있는지.
 * .spartaclub.kr 하위면 쿠키가 그대로 공유되므로 브릿지가 필요 없다.
 */
export const needsAuthBridge = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return isBridgedHost(window.location.hostname);
};

/** 브릿지가 토큰을 돌려보내도 되는 주소인지 확인한다. */
export const isAllowedReturnUrl = (value: string) => {
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      ALLOWED_RETURN_HOSTS.includes(url.hostname.toLowerCase())
    );
  } catch (e) {
    return false;
  }
};

/**
 * 로그인 후 돌아올 주소를 담아 구 도메인 브릿지 URL 을 만든다.
 * nextPath 는 현재 도메인 기준 경로다.
 */
export const buildBridgeUrl = (nextPath: string) => {
  const next = new URL(nextPath || '/', `${SITE_ORIGIN}/`).toString();
  return `${LEGACY_AUTH_ORIGIN}${BRIDGE_PATH}?next=${encodeURIComponent(next)}`;
};

/**
 * 정본 도메인에서 열린 로그인 페이지를 구 도메인의 같은 경로로 옮길 주소를 만든다.
 * initial_task·theme·kko_id 등 나머지 쿼리는 그대로 두고, 로그인 뒤 돌아갈 주소인
 * next 와 current 만 브릿지를 거치도록 감싼다.
 * 로그인은 구 도메인에서 해야 쿠키가 .spartaclub.kr 에 심기고, 브릿지를 거쳐야
 * 그 쿠키가 정본 도메인으로 넘어온다.
 */
export const buildLegacyAuthPageUrl = (
  pathname: string,
  searchParams: URLSearchParams,
) => {
  const target = new URL(pathname, LEGACY_AUTH_ORIGIN);
  searchParams.forEach((value, key) => {
    target.searchParams.append(key, value);
  });
  target.searchParams.set(
    'next',
    buildBridgeUrl(searchParams.get('next') || '/'),
  );
  // 카카오 신규 가입 화면은 쿼리의 current 를 가입 뒤 돌아갈 곳으로 쓴다.
  const current = searchParams.get('current');
  if (current) {
    target.searchParams.set('current', buildBridgeUrl(current));
  }
  return target.toString();
};

/**
 * Framer 페이지에 주입할 카카오 로그인 브릿지 스크립트.
 *
 * Framer 랜딩의 로그인 모달은 Next 의 LoginProcess 가 아니라 Framer Code Override 가
 * Kakao.Auth.authorize 를 직접 호출한다. 그대로 두면 state 의 next·current 가 정본
 * 도메인이라 콜백 뒤 쿠키가 넘어오지 않는다. authorize 호출 직전에 두 값을 구 도메인
 * 브릿지 경유 주소로 바꿔 Next 쪽 로그인과 같은 경로를 태운다.
 *
 * Kakao.Auth 는 Object.freeze 된 객체라 authorize 를 직접 바꿀 수 없다.
 * 대신 Kakao.init 이 this.Auth 에 대입하는 구조를 이용해 init 을 감싸고, init 직후
 * 원본을 프로토타입으로 둔 새 Auth 객체로 교체한다. 다른 메서드는 원본으로 위임된다.
 */
export const buildFramerKakaoBridgeScript = () => {
  const config = JSON.stringify({
    bridge: `${LEGACY_AUTH_ORIGIN}${BRIDGE_PATH}`,
    hosts: ALLOWED_RETURN_HOSTS,
  });
  return `(function (config) {
  var hosts = config.hosts;
  if (hosts.indexOf(window.location.hostname.toLowerCase()) === -1) {
    return;
  }
  var toBridge = function (value) {
    try {
      var url = new URL(value, window.location.origin);
      if (hosts.indexOf(url.hostname.toLowerCase()) === -1) {
        return value;
      }
      return config.bridge + '?next=' + encodeURIComponent(url.toString());
    } catch (e) {
      return value;
    }
  };
  var patchAuth = function (kakao) {
    var auth = kakao && kakao.Auth;
    if (!auth || auth.__authBridge) {
      return;
    }
    var bridged = Object.create(auth);
    Object.defineProperty(bridged, '__authBridge', { value: true });
    Object.defineProperty(bridged, 'authorize', {
      value: function (options) {
        var bridgedOptions = options;
        try {
          var state = JSON.parse(options.state);
          if (state && typeof state === 'object') {
            if (typeof state.next === 'string') {
              state.next = toBridge(state.next);
            }
            if (typeof state.current === 'string') {
              state.current = toBridge(state.current);
            }
            bridgedOptions = Object.assign({}, options, { state: JSON.stringify(state) });
          }
        } catch (e) {}
        return auth.authorize(bridgedOptions);
      },
    });
    kakao.Auth = bridged;
  };
  var kakao = window.Kakao;
  if (!kakao || (kakao.init && kakao.init.__authBridge)) {
    return;
  }
  if (typeof kakao.isInitialized === 'function' && kakao.isInitialized()) {
    patchAuth(kakao);
    return;
  }
  var originalInit = kakao.init;
  var bridgedInit = function () {
    var result = originalInit.apply(this, arguments);
    patchAuth(this);
    return result;
  };
  bridgedInit.__authBridge = true;
  kakao.init = bridgedInit;
})(${config});`;
};
